import { Trans } from '@lingui/macro'
import { Box } from '@mui/material'
import { Percent } from '@uniswap/sdk-core'
import { AddButton, BribesButton, ClaimButton, Pending } from 'components/Button'
import Column from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { IconLoadingBubble, LongLoadingBubble, SmallLoadingBubble } from 'components/Loading'
import Row, { RowBetween } from 'components/Row'
import { Cell, StyledTableRow } from 'components/Table'
import { ToastError } from 'components/Toast'
import Tooltip from 'components/Tooltip'
import { useActiveChainId } from 'connection/useActiveChainId'
import { useCurrency, useToken } from 'hooks/Tokens'
import { useMasterChefV3 } from 'hooks/useContract'
import { useListTokens } from 'pages/Vote/AddBribesModal/hooks'
import { TotalRewardsItem } from 'pages/Vote/VoteRow'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode, useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled, { css } from 'styled-components/macro'
import { ThemedText } from 'theme'
import { formatAmount } from 'utils/formatAmout'
import { formatCurrencyAmount, NumberType } from 'utils/formatCurrency'
import { handlerError } from 'utils/formatError'
import { unwrappedToken } from 'utils/unwrappedToken'

import { ReactComponent as ToIcon } from '../../assets/imgs/to2.svg'
import FarmPoolModal from './FarmPoolModal'
import { useSharsAmount } from './FarmPoolModal/Hooks'
import {
  LoadedRowProps,
  StyledFeeValue,
  StyledMaxLevleageValue,
  TooltipBox,
  TooltipRow,
  TotalLiquidityEarnsItem,
} from './LiquidityRow'
import { LiquiditySortMethod } from './state'

export const StyledTableRowM = styled(StyledTableRow)<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  width: 100%;
  padding: 16px 8px;
  margin: 0;
  gap: 16px;
  ${({ first, last }) => css`
    /* height: ${first || last ? '72px' : '64px'}; */
  `}
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:last-child {
    border: none;
  }

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }
`

export const TextLeftCell = styled(Cell)`
  justify-content: flex-start;
  flex: 1;
`

export const TextRightCell = styled(Cell)`
  justify-content: flex-end;
  text-align: right;
  flex: 1;
`

const DividerLine = styled(Box)`
  border-bottom: 1px dashed ${({ theme }) => theme.primaryBorder};
`

/* Token Row: skeleton row component */
function LiquidityRow({
  pools,
  tvl,
  apr,
  rewards,
  my_staked,
  earn,
  ...rest
}: {
  first?: boolean
  $loading?: boolean
  rewards: ReactNode
  tvl: ReactNode
  apr: ReactNode
  pools: ReactNode
  my_staked: ReactNode
  earn: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowCells = (
    <>
      <TextLeftCell data-testid="pool-cell">{pools}</TextLeftCell>
      <RowBetween gap="sm" align="flex-start">
        <TextLeftCell data-testid="tvl-cell">{tvl}</TextLeftCell>
        <TextRightCell data-testid="apr-cell">{apr}</TextRightCell>
      </RowBetween>
      <TextLeftCell data-testid="rewards-cell">{rewards}</TextLeftCell>
      <DividerLine />
      <TextLeftCell data-testid="my_staked-cell" className="">
        {my_staked}
      </TextLeftCell>
      <TextLeftCell data-testid="my-liquidity-cell" className="">
        {earn}
      </TextLeftCell>
    </>
  )
  return <StyledTableRowM {...rest}>{rowCells}</StyledTableRowM>
}

/* Loading State: row component with loading bubbles */
export function LoadingRow(props: { first?: boolean; last?: boolean }) {
  return (
    <LiquidityRow
      $loading
      pools={
        <RowBetween gap="sm">
          <Row gap="sm">
            <IconLoadingBubble width="40px" height="40px" />
            <SmallLoadingBubble />
          </Row>
          <ToIcon />
        </RowBetween>
      }
      tvl={
        <Column align="flex-start" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{LiquiditySortMethod.TVL}</Trans>
          </ThemedText.TextSecondary>
          <LongLoadingBubble />
        </Column>
      }
      apr={
        <Column align="flex-end" width="100%" gap="xs">
          <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
            <Trans>{LiquiditySortMethod.APR}</Trans>
          </ThemedText.TextSecondary>
          <LongLoadingBubble />
        </Column>
      }
      rewards={
        <Row justify="space-between" align="center" width="100%" gap="xs">
          <Column align="start" gap="xs">
            <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
              <Trans>{LiquiditySortMethod.INCENTIVIZATION}</Trans>
            </ThemedText.TextSecondary>
            <LongLoadingBubble />
          </Column>
          <BribesButton>+ Bribes</BribesButton>
        </Row>
      }
      my_staked={
        <Row justify="space-between" align="center" width="100%" gap="xs">
          <Column align="flex-start" gap="xs">
            <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
              <Trans>{LiquiditySortMethod.MY_STAKED}</Trans>
            </ThemedText.TextSecondary>
            <LongLoadingBubble />
          </Column>
          <AddButton>+/— Deposit</AddButton>
        </Row>
      }
      earn={
        <Column align="flex-start" width="100%" gap="sm">
          <Row justify="space-between" align="center" width="100%" gap="xs">
            <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
              <Trans>{LiquiditySortMethod.EARN}</Trans>
            </ThemedText.TextSecondary>
            <SmallLoadingBubble />
          </Row>
          <ClaimButton width="100%">Claim</ClaimButton>
        </Column>
      }
      {...props}
    />
  )
}

/* Loaded State: row component with token information */
export const LoadedRow = forwardRef((props: LoadedRowProps, ref: ForwardedRef<HTMLDivElement>) => {
  const { liquidityListIndex, liquidityListLength, liquidity, addBribeChage, showPoolMidalChage } = props

  const [showHover_TVL, setShowHover_TVL] = useState(false)
  const [showHover_rewards, setShowHover_rewards] = useState(false)

  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false)
  const [txHash, setTxHash] = useState<string>('')
  const [txError, setTxError] = useState<string>('')
  const pendingText = 'pending ...'
  const addTransaction = useTransactionAdder()

  const [showFarmPool, setShowFarmPool] = useState(false)
  const handleDismissCreate = () => {
    setShowFarmPool(false)
  }

  const masterChefContract = useMasterChefV3(liquidity.masterChefAddress)
  const tokens = useListTokens(masterChefContract)
  const { account } = useActiveChainId()
  const currrencyA = useCurrency(liquidity.token0.id)
  const currrencyB = useCurrency(liquidity.token1.id)
  const sharedAmount = useSharsAmount(liquidity.hypervisorAddress, currrencyA ?? undefined, currrencyB ?? undefined)

  const handleClaim = async () => {
    setAttemptingTxn(true)
    if (!masterChefContract || !liquidity || !account) return
    try {
      const tempList = liquidity.LiquidityrewardsLists?.map((item) => item?.tokenInfo?.id)
      if (!tempList) return
      const response = await masterChefContract.getReward(account)
      setAttemptingTxn(false)
      addTransaction(response, {
        type: TransactionType.CLAIM_REWARDS,
        token0Address: '',
        token1Address: '',
      })
      setTxHash(response.hash)
    } catch (error) {
      setAttemptingTxn(false)
      setTxError(handlerError(error))
      ToastError(handlerError(error))
      console.log('masterChefContract claimRewards error', error)
    }
  }

  const liquidityToken0 = useToken(liquidity.token0.id)
  const liquidityToken1 = useToken(liquidity.token1.id)
  const currency0 = liquidityToken0 ? unwrappedToken(liquidityToken0) : undefined
  const currency1 = liquidityToken1 ? unwrappedToken(liquidityToken1) : undefined

  return (
    <>
      <div ref={ref} data-testid={`liquidity-table-row-${liquidity.lpAddr}`}>
        <LiquidityRow
          pools={
            <RowBetween gap="sm">
              <Row gap="sm">
                <DoubleCurrencyLogo currency0={currency1} currency1={currency0} size={40} margin />
                <Column gap="xs" align="flex-start" ml="8px">
                  <ThemedText.TextPrimary fontWeight={700} fontSize={16}>
                    {liquidity.token0.symbol}-{liquidity.token1.symbol}
                  </ThemedText.TextPrimary>
                  {/* <ThemedText.TextPrimary fontWeight={700} fontSize={16}>
                    {new Percent(liquidity.poolFee, 1_000_000).toSignificant()}%
                  </ThemedText.TextPrimary> */}
                  <Box display="flex" alignItems="center">
                    <StyledMaxLevleageValue>{liquidity.maxLevleage}x</StyledMaxLevleageValue>
                    <StyledFeeValue>{new Percent(liquidity.poolFee, 1_000_000).toSignificant()}%</StyledFeeValue>
                  </Box>
                </Column>
              </Row>
              <ToIcon />
            </RowBetween>
          }
          tvl={
            <Column align="flex-start" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{LiquiditySortMethod.TVL}</Trans>
              </ThemedText.TextSecondary>
              <Column align="start" width="100%">
                <Tooltip
                  show={showHover_TVL}
                  text={
                    <TooltipBox>
                      <Row justify="space-between" alignItems="flex-start" className="tooltipItem">
                        {/* <ThemedText.TextPrimary fontWeight={700} className="text">
                          <Trans>Wallet</Trans>
                        </ThemedText.TextPrimary> */}
                        <ThemedText.TextSecondary fontSize={14}>
                          {formatCurrencyAmount(liquidity?.liqvidityToken0, NumberType.FiatTokenStats2)}{' '}
                          {liquidity.token0.symbol}
                        </ThemedText.TextSecondary>
                      </Row>
                      <Row justify="space-between" alignItems="flex-start" className="tooltipItem">
                        {/* <ThemedText.TextPrimary fontWeight={700} className="text">
                          <Trans>Staked</Trans>
                        </ThemedText.TextPrimary> */}
                        <ThemedText.TextSecondary fontSize={14}>
                          {formatCurrencyAmount(liquidity.liqvidityToken1, NumberType.FiatTokenStats2)}{' '}
                          {liquidity.token1.symbol}
                        </ThemedText.TextSecondary>
                      </Row>
                    </TooltipBox>
                  }
                >
                  <TooltipRow onMouseEnter={() => setShowHover_TVL(true)} onMouseLeave={() => setShowHover_TVL(false)}>
                    <ThemedText.TextPrimary fontWeight={700}>
                      ${formatAmount(liquidity.tvl, 2, true)}
                    </ThemedText.TextPrimary>
                  </TooltipRow>
                </Tooltip>
              </Column>
            </Column>
          }
          apr={
            <Column align="flex-end" width="100%" gap="xs">
              <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                <Trans>{LiquiditySortMethod.APR}</Trans>
              </ThemedText.TextSecondary>
              <ThemedText.TextPrimary fontWeight={700}>
                {formatAmount(Number(liquidity.liquityAPR) * 100, 2, true)}%
              </ThemedText.TextPrimary>
            </Column>
          }
          rewards={
            <Row justify="space-between" align="center" width="100%" gap="xs">
              <Column align="start" gap="xs">
                <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                  <Trans>{LiquiditySortMethod.INCENTIVIZATION}</Trans>
                </ThemedText.TextSecondary>
                <ThemedText.TextPrimary fontWeight={700}>
                  ${formatAmount(liquidity.LiquidityrewardUSD, 2, true)}
                </ThemedText.TextPrimary>
                {liquidity?.LiquidityrewardsLists?.map((item, index) => {
                  return <TotalRewardsItem key={'liquidity-rewards-item' + index} rewardAddr={item}></TotalRewardsItem>
                })}
              </Column>
              <BribesButton onClick={() => addBribeChage(liquidity)}>+ Bribes</BribesButton>
            </Row>
          }
          my_staked={
            <Row justify="space-between" align="center" width="100%" gap="xs">
              <Column align="flex-start" gap="xs">
                <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                  <Trans>{LiquiditySortMethod.MY_STAKED}</Trans>
                </ThemedText.TextSecondary>
                <ThemedText.TextPrimary fontWeight={700}>
                  ${formatAmount(liquidity.stakeData.tvl, 2, true)}
                </ThemedText.TextPrimary>
                <ThemedText.TextSecondary fontSize={14}>
                  {formatCurrencyAmount(sharedAmount?.amount0, NumberType.FiatTokenStats2)} {liquidity.token0.symbol}
                </ThemedText.TextSecondary>
                <ThemedText.TextSecondary fontSize={14}>
                  {formatCurrencyAmount(sharedAmount?.amount1, NumberType.FiatTokenStats2)} {liquidity.token1.symbol}
                </ThemedText.TextSecondary>
              </Column>
              <AddButton onClick={() => setShowFarmPool(true)}>+/— Deposit</AddButton>
            </Row>
          }
          earn={
            <Column align="flex-start" width="100%" gap="sm">
              <Row justify="space-between" align="center" width="100%" gap="xs">
                <ThemedText.TextSecondary fontSize={12} fontWeight={700}>
                  <Trans>{LiquiditySortMethod.EARN}</Trans>
                </ThemedText.TextSecondary>
                <Column align="flex-end" gap="xs">
                  {tokens && tokens?.length > 0 ? (
                    tokens?.map((item: any, index: number) => {
                      return (
                        <TotalLiquidityEarnsItem
                          key={'earn-liquidity' + index}
                          token={item}
                          masterchefAddr={liquidity.masterChefAddress}
                        />
                      )
                    })
                  ) : (
                    <ThemedText.TextRewards>--</ThemedText.TextRewards>
                  )}
                </Column>
              </Row>
              <ClaimButton width="100%" disabled={attemptingTxn} onClick={handleClaim}>
                {attemptingTxn ? <Pending /> : 'Claim'}
              </ClaimButton>
            </Column>
          }
          first={liquidityListIndex === 0}
          last={liquidityListIndex === liquidityListLength - 1}
        />
      </div>
      <FarmPoolModal
        poolAddress={liquidity.lpAddr}
        token0={liquidity.token0}
        token1={liquidity.token1}
        fee={liquidity.poolFee}
        isOpen={showFarmPool}
        hypervisorAddr={liquidity.hypervisorAddress}
        onDismiss={handleDismissCreate}
      />
    </>
  )
})

LoadedRow.displayName = 'LoadedRow'
